// HAWELO — shared components
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

export function SvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        {/* Intentionally empty — rough-edge filters removed for a clean look.
            Decorative hand-drawn shapes are done with real SVG paths instead. */}
      </defs>
    </svg>
  );
}

export function Placeholder({ label, onDark, style, className = '' }) {
  return (
    <div className={`placeholder ${onDark ? 'on-dark' : ''} ${className}`} style={style}>
      <span>{label}</span>
    </div>
  );
}

// Preserved PNG-based HA-WE-LO wordmark (user requested the current logo be kept)
// 3 parts are clickable with a soft bounce loop so it reads as interactive.
export const PHILOSOPHY = {
  ha: {
    tag: 'HA',
    title: 'Healthy Habit',
    accent: '#E8546A',
    intro: 'Good health comes from daily habits.',
    body: 'Wellness is not a week-long reset. It is a small honest choice, made the same way every morning. Our food rewards that repetition with texture, taste, and trust.',
    pills: ['Ancient Grains', 'Low GI Foods', 'Gut Health'],
  },
  we: {
    tag: 'WE',
    title: 'Wellness Everyday',
    accent: '#1E2B8C',
    intro: 'Wellness is more than nutrition.',
    body: 'Every batch is cooked, dried, or pressed in small runs so nothing waits long enough to lose itself. What reaches your shelf is what left our kitchen — no staler, no sweeter, no softer.',
    pills: ['Better Sleep', 'Mental Clarity', 'Body Balance'],
  },
  lo: {
    tag: 'LO',
    title: 'Love for Real Food',
    accent: '#4CAF50',
    intro: 'Food carries the care of everyone involved.',
    body: 'We source from farms we have walked, from people we have met. When ingredients are loved before they are packed, you can taste the difference. Real food has a handwriting — we let ours show.',
    pills: ['Farm Partners', 'Fair Prices', 'Traditions'],
  },
};

export function Wordmark({ height = 42, onPartClick, interactive = false }) {
  const parts = ['ha', 'we', 'lo'];
  const bounce = (delay) => ({
    animate: {
      y: [0, -4, 0],
      transition: {
        duration: 0.5,
        repeat: Infinity,
        repeatDelay: 6 + (delay / 1000),
        ease: 'easeInOut',
        delay,
      },
    },
  });

  if (!interactive) {
    return (
      <div className="wordmark-png" style={{ height, '--h': `${height}px` }} data-testid="hawelo-wordmark">
        {parts.map((p, i) => (
          <div
            key={p}
            className={`wordmark-part wm-${p}`}
            style={{
              WebkitMaskImage: `url(/assets/${p}.png)`,
              maskImage: `url(/assets/${p}.png)`,
              animationDelay: `${i * 90}ms`,
            }}
            aria-label={p}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="wordmark-png interactive" style={{ height, '--h': `${height}px` }} data-testid="hawelo-wordmark">
      {parts.map((p, i) => (
        <motion.button
          key={p}
          type="button"
          className={`wordmark-btn wm-btn-${p}`}
          style={{ height: '100%' }}
          variants={bounce(2 + i * 1.5)}
          animate="animate"
          whileHover={{ scale: 1.08, y: -3 }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => { e.stopPropagation(); onPartClick && onPartClick(p); }}
          aria-label={`${p.toUpperCase()} — ${PHILOSOPHY[p].title}`}
          data-testid={`logo-part-${p}`}
        >
          <span
            className={`wordmark-part wm-${p}`}
            style={{
              WebkitMaskImage: `url(/assets/${p}.png)`,
              maskImage: `url(/assets/${p}.png)`,
            }}
          />
        </motion.button>
      ))}
    </div>
  );
}

export function PhilosophyPopup({ part, onClose }) {
  const data = PHILOSOPHY[part];
  if (!data) return null;
  return (
    <motion.div
      className="popup-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="philosophy-popup-overlay"
    >
      <motion.div
        className="popup-card"
        initial={{ scale: 0.9, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.92, opacity: 0, y: 16 }}
        transition={{ type: 'spring', damping: 26, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        data-testid={`philosophy-popup-${part}`}
        style={{ '--accent': data.accent }}
      >
        <button
          type="button"
          className="popup-close"
          onClick={onClose}
          aria-label="Close"
          data-testid="philosophy-popup-close"
        >
          <Icon kind="x" size={22} color="#1E2B8C" />
        </button>
        <div className="popup-tag" style={{ color: data.accent }}>{data.tag}</div>
        <h2 className="popup-title" style={{ color: data.accent }}>{data.title}</h2>
        <p className="popup-intro">{data.intro}</p>
        <p className="popup-body">{data.body}</p>
        <div className="popup-pills">
          {data.pills.map((pill) => (
            <span key={pill} className="popup-pill">{pill}</span>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

export { AnimatePresence };

export function Icon({ kind, size = 48, color = '#F5F0E8' }) {
  const base = { width: size, height: size, stroke: color, strokeWidth: 2, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (kind === 'millet') return (
    <svg viewBox="0 0 48 48" {...base}>
      <path d="M24 6 v36" />
      <path d="M24 12 c-6 2 -10 6 -10 10" />
      <path d="M24 12 c6 2 10 6 10 10" />
      <path d="M24 22 c-5 2 -8 5 -8 8" />
      <path d="M24 22 c5 2 8 5 8 8" />
      <path d="M24 32 c-3 1 -5 2 -5 4" />
      <path d="M24 32 c3 1 5 2 5 4" />
    </svg>
  );
  if (kind === 'berries') return (
    <svg viewBox="0 0 48 48" {...base}>
      <circle cx="16" cy="28" r="7" />
      <circle cx="28" cy="32" r="6" />
      <circle cx="34" cy="22" r="5" />
      <path d="M20 14 l-4 -6 M28 14 l-2 -8 M36 18 l4 -6" />
    </svg>
  );
  if (kind === 'nuts') return (
    <svg viewBox="0 0 48 48" {...base}>
      <ellipse cx="18" cy="28" rx="8" ry="10" transform="rotate(-20 18 28)" />
      <ellipse cx="32" cy="24" rx="7" ry="9" transform="rotate(25 32 24)" />
      <path d="M14 26 q4 -4 8 0" />
      <path d="M30 20 q3 -3 6 0" />
    </svg>
  );
  if (kind === 'heart') return (
    <svg viewBox="0 0 48 48" {...base}>
      <path d="M24 40 C 10 30 6 22 10 16 C 14 10 22 12 24 18 C 26 12 34 10 38 16 C 42 22 38 30 24 40 z" />
    </svg>
  );
  if (kind === 'warmmilk') return (
    <svg viewBox="0 0 48 48" {...base}>
      <path d="M12 20 h24 v18 a6 6 0 0 1 -6 6 h-12 a6 6 0 0 1 -6 -6 z" />
      <path d="M36 24 h4 a4 4 0 0 1 0 8 h-4" />
      <path d="M18 10 q2 2 0 4 M24 8 q2 2 0 4 M30 10 q2 2 0 4" />
    </svg>
  );
  if (kind === 'coldmilk') return (
    <svg viewBox="0 0 48 48" {...base}>
      <path d="M14 18 l2 22 a4 4 0 0 0 4 4 h8 a4 4 0 0 0 4 -4 l2 -22 z" />
      <path d="M14 18 h20" />
      <path d="M20 28 l3 -3 l3 3" />
      <path d="M26 32 l3 -3 l3 3" />
    </svg>
  );
  if (kind === 'yogurt') return (
    <svg viewBox="0 0 48 48" {...base}>
      <path d="M12 18 h24 l-2 22 a4 4 0 0 1 -4 4 h-12 a4 4 0 0 1 -4 -4 z" />
      <path d="M16 24 c4 -2 8 2 16 0" />
      <circle cx="24" cy="12" r="2" />
      <circle cx="32" cy="10" r="1.5" />
    </svg>
  );
  if (kind === 'smoothie') return (
    <svg viewBox="0 0 48 48" {...base}>
      <path d="M10 24 a14 10 0 0 0 28 0 z" />
      <path d="M14 24 h20" />
      <circle cx="18" cy="20" r="2" />
      <circle cx="26" cy="18" r="2.5" />
      <circle cx="32" cy="22" r="1.8" />
      <path d="M24 14 v-4" />
    </svg>
  );
  if (kind === 'cart') return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4 h3 l2.5 13 h11 l2 -9 h-13" />
      <circle cx="10" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
    </svg>
  );
  if (kind === 'search') return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20 l-4.5 -4.5" />
    </svg>
  );
  if (kind === 'menu') return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <path d="M4 7 h16 M4 12 h16 M4 17 h16" />
    </svg>
  );
  if (kind === 'home') return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11 l9 -7 l9 7" />
      <path d="M5 10 v10 h5 v-6 h4 v6 h5 v-10" />
    </svg>
  );
  if (kind === 'grid') return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="8" height="8" />
      <rect x="13" y="3" width="8" height="8" />
      <rect x="3" y="13" width="8" height="8" />
      <rect x="13" y="13" width="8" height="8" />
    </svg>
  );
  if (kind === 'x') return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <path d="M6 6 l12 12 M18 6 l-12 12" />
    </svg>
  );
  return null;
}

export function ProductCard({ product, onAdd, onOpen }) {
  const { handle, title, price, currency, featuredImage, variants } = product;
  const addId = variants?.[0]?.id;
  return (
    <article
      className="product-card"
      onClick={() => onOpen(handle)}
      data-testid={`product-card-${handle}`}
    >
      <div className="natural-badge">110% Natural</div>
      <div className="p-image">
        {featuredImage?.url ? (
          <img
            src={featuredImage.url}
            alt={featuredImage.altText || title}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Placeholder label={`pouch shot\n${title}`} />
        )}
      </div>
      <h3 className="product-name">{title}</h3>
      {product.description && (
        <p className="product-desc">{product.description.slice(0, 80)}{product.description.length > 80 ? '…' : ''}</p>
      )}
      <div className="product-meta">
        <span className="product-price">
          {currency === 'INR' ? `₹${Math.round(price)}` : `${currency} ${price.toFixed(2)}`}
        </span>
        <button
          className="imp-btn small"
          onClick={(e) => { e.stopPropagation(); if (onAdd && addId) onAdd(addId); }}
          data-testid={`add-to-cart-${handle}`}
          disabled={!product.availableForSale}
        >
          {product.availableForSale ? 'Add' : 'Soldout'}
        </button>
      </div>
    </article>
  );
}
